import React, {
	ComponentType,
	forwardRef,
	HTMLAttributes,
	ReactNode,
} from 'react';
import styled from '@emotion/styled';
import { Grid, GridItem, ITheme } from '../../../../../../ui';
import { BookingSummary, StyledBookingSummary } from './BookingSummary';
import { BookingSummaryMobileFooter } from './BookingSummaryMobileFooter';
import { getOverlapWithHeaderStyles } from '../../../../../shared/components';
import { getTotalNewBookingPrice } from '../../../../domain/booking/models/helpers/getTotalNewBookingPrice';
import { getOriginalGrandTotalPrice } from '../../../../domain/booking/models/helpers/getOriginalGrandTotalPrice';
import { IBooking } from '../../../../domain/booking/models/IBooking';
import { INewBooking } from '../../../../domain/booking/models/INewBooking';
import themed from '../../../../../../ui/themer/themed';
import ContentWrap from '../../../../../../ui/components/Layout/ContentWrap';
import Responsive from '../../../../../../ui/utils/Responsive';
import { IBookingSummarySubmitProps } from './IBookingSummarySubmitProps';

const MainContentWrap = styled.div``;

export const StyledBookingSummaryLayout = styled(ContentWrap)<{}, ITheme>`
	${getOverlapWithHeaderStyles}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin: 0;

		${StyledBookingSummary} {
			display: none;
		}

		${MainContentWrap} {
			margin: ${themed(({ layout }) => layout.main.margin.mobile)};
		}
	}
`;

export interface IBookingSummaryLayoutProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	Submit?: ComponentType<IBookingSummarySubmitProps>;
	originalBooking?: IBooking;
	booking?: INewBooking;
	className?: string;
	changeButtonUrl?: string;
	hidePaymentSection?: boolean;
}

export const BookingSummaryLayout = forwardRef<
	HTMLDivElement,
	IBookingSummaryLayoutProps
>(
	(
		{
			children,
			originalBooking,
			booking,
			Submit,
			className,
			changeButtonUrl,
			hidePaymentSection,
		}: IBookingSummaryLayoutProps,
		ref,
	) => {
		const originalTotalPrice =
			originalBooking && getOriginalGrandTotalPrice(originalBooking);
		const totalPrice = booking && getTotalNewBookingPrice(booking);
		const earningLoyaltyPoints = booking?.parkingProduct?.loyaltyPoints;

		return (
			<StyledBookingSummaryLayout className={className} ref={ref}>
				<BookingSummaryGrid hidePaymentSection={hidePaymentSection}>
					<GridItem
						columns={[8, { mobileXL: 12 }]}
						style={hidePaymentSection ? { order: 2 } : undefined}
					>
						<MainContentWrap>{children}</MainContentWrap>
					</GridItem>
					<GridItem
						columns={[4, { mobileXL: 0 }]}
						style={hidePaymentSection ? { order: 1 } : undefined}
					>
						{booking && (
							<StyledStickyContainer>
								<BookingSummary
									originalBooking={originalBooking}
									booking={booking}
									Submit={Submit}
									changeButtonUrl={changeButtonUrl}
								/>
							</StyledStickyContainer>
						)}
					</GridItem>
				</BookingSummaryGrid>

				{booking && totalPrice && (
					<BookingSummaryMobileFooter
						originalTotalPrice={originalTotalPrice}
						totalPrice={totalPrice}
						loyaltyPoints={earningLoyaltyPoints}
						Submit={Submit}
						booking={booking}
						changeButtonUrl={changeButtonUrl}
					/>
				)}
			</StyledBookingSummaryLayout>
		);
	},
);

const BookingSummaryGrid = styled(Grid)<
	{ hidePaymentSection?: boolean },
	ITheme
>`
	/* Override the default grid item styling to allow the Booking Summary column the grow when needed */
	${GridItem}:first-of-type {
		flex-grow: 1;
		flex-basis: 0;

		${({ hidePaymentSection }) =>
			hidePaymentSection && `align-content: flex-end;`}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding-bottom: 350px;
		flex-direction: column;
	}
`;

const StyledStickyContainer = styled.div`
	position: sticky;
	top: 16px;
`;
